import React, { useState, useEffect } from 'react'
import { router } from '@inertiajs/react'
import { AppShell } from '@/components/shared/AppShell'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Target, Filter, BarChart3, Settings, Eye, Rocket,
  ChevronRight, ChevronLeft, Sparkles, Check, Loader2, Save
} from 'lucide-react'

// Step Components
import StepTestType from './steps/StepTestType'
import StepExamSyllabus from './steps/StepExamSyllabus'
import StepPoolFilters from './steps/StepPoolFilters'
import StepWeightage from './steps/StepWeightage'
import StepMarkingScheme from './steps/StepMarkingScheme'
import StepPoolPreview from './steps/StepPoolPreview'
import StepReviewGenerate from './steps/StepReviewGenerate'

// ─── Data Types ───────────────────────────────────────────────────────

export interface SyllabusSubjectData {
  id: number
  name: string
  subject_id: number
  questions_count: number
  total_in_subject: number
  excluded_count: number
  chapters: {
    id: number
    name: string
    chapter_id: number
    standards: { id: number; name: string }[]
    questions_count: number
    total_in_chapter: number
    excluded_count: number
    sections: {
      id: number
      name: string
      section_ids: number[]
      questions_count: number
      total_in_section: number
      excluded_count: number
    }[]
  }[]
}

export interface SyllabusData {
  id: number
  name: string
  version: number
  total_questions: number
  total_in_syllabus: number
  excluded_count: number
  subjects: SyllabusSubjectData[]
}

export interface ExamOption {
  id: number
  name: string
  code: string
  year: number | null
  config?: {
    timer_type?: string
    calculator_enabled?: boolean
    marked_for_review_rule?: string
    pre_test_instructions_html?: string
  } | null
  marking_rules?: {
    id: number
    name: string
    question_type: string
    scoring_strategy: string
    scoring_params: any
  }[]
  test_types?: {
    id: number
    name: string
    test_type_key: string
    total_questions: number
    duration_minutes: number | null
    positive_marks: number
    negative_marks: number
    exam_marking_rule_id?: number | null
    marking_rule?: {
      id: number
      name: string
      scoring_strategy: string
      scoring_params: any
    } | null
    description?: string | null
    instructions_html?: string | null
    sort_order: number
    sections?: {
      id: number
      name: string
      sort_order: number
      total_questions: number
      max_attempts?: number | null
      duration_minutes?: number | null
      subject_hint?: string | null
      exam_marking_rule_id?: number | null
      subject_id?: number | null
      subject_ids?: number[]
      chapter_ids?: number[]
      subject?: { id: number; name: string } | null
      scope_deferred: boolean
      deferred_scope_type?: 'chapter' | 'subject' | null
    }[]
  }[]
}

export interface PoolBreakdown {
  total: number
  by_subject: Record<string, { name: string; count: number }>
  by_chapter: Record<string, { name: string; subject: string; count: number }>
  by_standard?: Record<string, number>
  by_difficulty: Record<string, number>
  by_type: Record<string, number>
}

export interface SubjectWeightage {
  subject_id: number
  name: string
  percentage: number
  marks: number
  calculated_questions: number
  available: number
  chapters: {
    chapter_id: number
    name: string
    percentage: number
    available: number
    calculated_questions: number
  }[]
}

export interface SelectedQuestion {
  id: number
  question_text_preview: string
  difficulty: string
  question_type: string
  subject?: { id: number; name: string }
  chapter?: { id: number; name: string }
  section?: { id: number; name: string; sort_order: number }
  positive_marks: number
  negative_marks: number
  quality_score?: number
  is_pyq: boolean
}

export interface ChapterWeightage {
  chapter_id: number
  name: string
  percentage: number
  calculated_questions: number
  available: number
}

export type WeightageMode = 'percentage' | 'marks' | 'questions'
export type TestMode = 'full_mock' | 'sectional' | 'subject_wise' | 'chapter_wise' | 'topic_wise' | 'custom'

export interface WizardState {
  // Step 0: Exam & Syllabus
  exam_id: number | null
  selectedSyllabiIds: number[]
  syllabiData: SyllabusData[]
  language: string

  // Step 1: Test Type & Scope (loaded dynamically from exam)
  testType: string
  exam_test_type_id: number | null
  resolvedSectionParams: {
    sectionId: number
    selectedSubjectId?: number | null
    selectedChapterId?: number | null
  }[]
  testMode: TestMode
  difficultyLevel: string | null
  blueprintSections: any[]

  // Step 2: Pool Filters
  filterSubjectIds: number[]
  filterChapterIds: number[]
  filterSectionIds: number[]
  filterStandardIds: number[]
  filterDifficulties: string[]
  filterQuestionTypes: string[]
  includeTags: string[]
  excludeTags: string[]
  excludeTestIds: number[]
  pyqOnly: boolean
  exemplarOnly: boolean

  // Resulting Pool Data
  poolBreakdown: PoolBreakdown | null

  // Step 3: Marking Scheme
  markingScheme: {
    selected_rule_id?: number | 'custom' | null
    rule_name?: string | null
    positive_marks: number
    negative_marks: number
    negative_marking_enabled: boolean
    scoring_strategy?: string
    scoring_params?: Record<string, any>
    section_marking_rules?: Record<number, number>
  }
  sectionDurations?: Record<string | number, number | null>

  // Step 4: Metadata & Weightage
  title: string
  description: string
  durationMinutes: number | null
  noDuration: boolean
  timerType?: 'global' | 'sectional' | 'none' | string
  markedForReviewRule?: 'counts_if_answered' | 'never_counts' | 'always_counts' | string
  calculatorEnabled?: boolean
  preTestInstructionsHtml?: string
  totalQuestions: number
  totalMarks: number
  weightageMode: WeightageMode
  subjectWeightages: SubjectWeightage[]
  difficultyMix: { Easy: number; Medium: number; Hard: number }

  // Step 5: Pool Selection & Preview
  selectionMode: 'auto' | 'manual' | 'hybrid'
  selectedQuestions: SelectedQuestion[]

  // Step 6: Test Access & Pricing
  isFree: boolean
  creditCost: number

  // Editing existing test
  testId?: number | null
}

const INITIAL_STATE: WizardState = {
  exam_id: null,
  selectedSyllabiIds: [],
  syllabiData: [],
  language: 'en',
  testType: '',
  exam_test_type_id: null,
  resolvedSectionParams: [],
  testMode: 'full_mock',
  difficultyLevel: null,
  blueprintSections: [],
  filterSubjectIds: [],
  filterChapterIds: [],
  filterSectionIds: [],
  filterStandardIds: [],
  filterDifficulties: [],
  filterQuestionTypes: [],
  includeTags: [],
  excludeTags: [],
  excludeTestIds: [],
  pyqOnly: false,
  exemplarOnly: false,
  poolBreakdown: null,
  title: '',
  description: '',
  durationMinutes: 180,
  noDuration: false,
  timerType: 'global',
  markedForReviewRule: 'counts_if_answered',
  calculatorEnabled: false,
  preTestInstructionsHtml: '',
  totalQuestions: 0,
  totalMarks: 0,
  weightageMode: 'percentage',
  subjectWeightages: [],
  difficultyMix: { Easy: 0, Medium: 0, Hard: 0 },
  markingScheme: {
    positive_marks: 4,
    negative_marks: 1,
    negative_marking_enabled: true,
  },
  sectionDurations: {},
  selectionMode: 'auto',
  selectedQuestions: [],
  isFree: true,
  creditCost: 0,
  testId: null,
}

// ─── Step Definitions ────────────────────────────────────────────────

const STEPS = [
  { key: 'exam_syllabus', label: 'Exam & Syllabus', icon: Target, subtitle: 'Select target exam & syllabus' },
  { key: 'test_type', label: 'Test Type', icon: Sparkles, subtitle: 'Select exam-specific test type' },
  { key: 'pool_filters', label: 'Pool Filters', icon: Filter, subtitle: 'Refine questions & taxonomy' },
  { key: 'marking', label: 'Marking Scheme', icon: Settings, subtitle: 'Set grading rules' },
  { key: 'weightage', label: 'Weightage', icon: BarChart3, subtitle: 'Configure distribution' },
  { key: 'preview', label: 'Pool Preview', icon: Eye, subtitle: 'Verify question pool' },
  { key: 'review', label: 'Review & Generate', icon: Rocket, subtitle: 'Final test creation' },
]

// ─── Props ───────────────────────────────────────────────────────────

interface Props {
  exams: ExamOption[]
  standards: { id: number; name: string }[]
  tags: string[]
  editingTest?: any
}

// ─── Component ───────────────────────────────────────────────────────

export default function TestGeneratorIndex({ exams = [], standards = [], tags = [], editingTest }: Props) {
  const [currentStep, setCurrentStep] = useState(0)
  const [state, setState] = useState<WizardState>(INITIAL_STATE)
  const [generating, setGenerating] = useState(false)
  const [generateError, setGenerateError] = useState<string | null>(null)

  const updateState = (patch: Partial<WizardState>) => {
    setState(prev => ({ ...prev, ...patch }))
  }

  useEffect(() => {
    if (editingTest) {
      updateState({
        testId: editingTest.id,
        title: editingTest.title || '',
        description: editingTest.description || '',
        preTestInstructionsHtml: editingTest.pre_test_instructions_html || editingTest.generation_criteria?.instructions_html || '',
        testType: editingTest.test_type || 'full_mock',
        testMode: editingTest.generation_criteria?.test_mode || 'full_mock',
        exam_test_type_id: editingTest.exam_test_type_id || editingTest.generation_criteria?.exam_test_type_id || null,
        difficultyLevel: editingTest.difficulty_level || null,
        language: editingTest.language || 'en',
        durationMinutes: editingTest.duration_minutes || 180,
        noDuration: !editingTest.duration_minutes && editingTest.timer_type === 'none',
        timerType: editingTest.timer_type || 'global',
        exam_id: editingTest.exam_id,
        isFree: editingTest.is_free !== false,
        creditCost: editingTest.credit_cost || 0,
        sectionDurations: editingTest.section_durations || {},
        blueprintSections: editingTest.blueprint_sections || [],
        markingScheme: editingTest.marking_scheme || INITIAL_STATE.markingScheme,
        selectedQuestions: editingTest.selected_questions || [],
        selectedSyllabiIds: editingTest.generation_criteria?.syllabus_ids || [],
        subjectWeightages: editingTest.generation_criteria?.subject_weightages || [],
        difficultyMix: editingTest.generation_criteria?.difficulty_mix || { Easy: 0, Medium: 0, Hard: 0 },
        totalQuestions: editingTest.selected_questions?.length || 0,
        filterSubjectIds: editingTest.generation_criteria?.filters?.subject_ids?.map(Number) || [],
        filterChapterIds: editingTest.generation_criteria?.filters?.chapter_ids?.map(Number) || [],
        filterSectionIds: editingTest.generation_criteria?.filters?.section_ids?.map(Number) || [],
        filterStandardIds: editingTest.generation_criteria?.filters?.standard_ids?.map(Number) || [],
        filterDifficulties: editingTest.generation_criteria?.filters?.difficulties || [],
        filterQuestionTypes: editingTest.generation_criteria?.filters?.question_types || [],
        includeTags: editingTest.generation_criteria?.filters?.include_tags || [],
        excludeTags: editingTest.generation_criteria?.filters?.exclude_tags || [],
        pyqOnly: editingTest.generation_criteria?.filters?.flags?.pyq_only || false,
        exemplarOnly: editingTest.generation_criteria?.filters?.flags?.exemplar_only || false,
      })
    }
  }, [editingTest])

  const isStepComplete = (idx: number): boolean => {
    switch (idx) {
      case 0:
        // Exam & Syllabus
        return state.exam_id !== null && state.selectedSyllabiIds.length > 0
      case 1: {
        // Test Type & Scope
        if (!state.testType) return false
        const selectedTT = exams.find(e => e.id === state.exam_id)
                                ?.test_types?.find(tt => tt.id === state.exam_test_type_id)
        const deferredSections = (selectedTT?.sections || []).filter(s => s.scope_deferred)
        if (deferredSections.length > 0) {
          return deferredSections.every(sec => {
            const resolved = state.resolvedSectionParams.find(r => r.sectionId === sec.id)
            if (!resolved?.selectedSubjectId) return false
            if (sec.deferred_scope_type === 'chapter' && !resolved?.selectedChapterId) return false
            return true
          })
        }
        return true
      }
      case 2:
        // Pool Filters: Pool must be fetched and have questions
        return state.poolBreakdown !== null && state.poolBreakdown.total > 0
      case 3:
        // Marking Scheme
        return (
          state.markingScheme.positive_marks > 0 &&
          (!state.markingScheme.negative_marking_enabled || state.markingScheme.negative_marks > 0)
        )
      case 4: {
        // Weightage
        const poolAvailable = state.poolBreakdown?.total ?? 0
        const isWeightageValid =
          state.subjectWeightages.length === 0 ||
          state.subjectWeightages.every(sw => sw.calculated_questions <= sw.available)
        const questionsSum = state.subjectWeightages.reduce((sum, sw) => sum + (sw.calculated_questions || 0), 0)
        return (
          state.title.trim() !== '' &&
          state.totalQuestions > 0 &&
          state.totalQuestions <= poolAvailable &&
          isWeightageValid &&
          (state.subjectWeightages.length === 0 || questionsSum === state.totalQuestions)
        )
      }
      case 5:
        // Pool Preview
        return state.selectedQuestions.length > 0
      case 6:
        // Review & Generate
        return state.selectedQuestions.length > 0
      default:
        return false
    }
  }

  const isStepAccessible = (targetIdx: number): boolean => {
    if (targetIdx <= currentStep) return true
    for (let i = 0; i < targetIdx; i++) {
      if (!isStepComplete(i)) return false
    }
    return true
  }

  const canGoNext = () => isStepComplete(currentStep)

  const handleGenerateTest = async () => {
    const selectedCount = state.selectedQuestions.length
    if (selectedCount === 0 || generating) return

    setGenerating(true)
    setGenerateError(null)

    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
      const isPractice = state.testType === 'practice'
      const res = await fetch('/admin/test_generator/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': token,
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          test_id: state.testId,
          title: state.title,
          description: state.description,
          test_type: state.testType,
          test_mode: state.testMode,
          difficulty_level: state.difficultyLevel,
          duration_minutes: (isPractice || state.noDuration) ? null : state.durationMinutes,
          language: state.language,
          exam_id: state.exam_id,
          exam_test_type_id: state.exam_test_type_id,
          question_ids: state.selectedQuestions.map(q => q.id),
          positive_marks: state.markingScheme.positive_marks,
          negative_marks: (isPractice || !state.markingScheme.negative_marking_enabled)
            ? 0
            : state.markingScheme.negative_marks,
          negative_marking_enabled: isPractice ? false : state.markingScheme.negative_marking_enabled,
          section_marking_rules: state.markingScheme.section_marking_rules,
          section_durations: state.sectionDurations,
          sections: (state.blueprintSections || []).filter((bs: any) => bs._selected !== false).map((sec: any) => {
            const resolved = (state.resolvedSectionParams || []).find(r => r.sectionId === sec.id)
            return {
              ...sec,
              exam_test_type_section_id: sec.exam_test_type_section_id !== undefined
                ? sec.exam_test_type_section_id
                : (sec.exam_section_template_id ? null : (state.exam_test_type_id ? sec.id : null)),
              exam_section_template_id: sec.exam_section_template_id || null,
              subject_id: resolved?.selectedSubjectId || sec.subject_id,
              chapter_ids: resolved?.selectedChapterId ? [resolved.selectedChapterId] : sec.chapter_ids
            }
          }),
          timer_type: (isPractice || state.noDuration) ? 'none' : state.timerType,
          marked_for_review_rule: state.markedForReviewRule,
          calculator_enabled: state.calculatorEnabled,
          pre_test_instructions_html: state.preTestInstructionsHtml,
          weightage_mode: state.weightageMode,
          subject_weightages: state.subjectWeightages,
          difficulty_mix: state.difficultyMix,
          syllabus_ids: state.selectedSyllabiIds,
          is_free: state.isFree,
          credit_cost: state.isFree ? 0 : state.creditCost
        })
      })

      const data = await res.json()
      if (!res.ok || data.error) {
        setGenerateError(data.error || 'Failed to generate test.')
        setGenerating(false)
      } else if (data.redirect_to) {
        router.visit(data.redirect_to)
      }
    } catch (err: any) {
      setGenerateError(err.message || 'An error occurred during test creation.')
      setGenerating(false)
    }
  }

  const stepComponents = [
    <StepExamSyllabus key="s0" state={state} updateState={updateState} exams={exams} />,
    <StepTestType key="s1" state={state} updateState={updateState} exams={exams} />,
    <StepPoolFilters key="s2" state={state} updateState={updateState} standards={standards} tags={tags} />,
    <StepMarkingScheme key="s3" state={state} updateState={updateState} exams={exams} />,
    <StepWeightage key="s4" state={state} updateState={updateState} />,
    <StepPoolPreview key="s5" state={state} updateState={updateState} />,
    <StepReviewGenerate
      key="s6"
      state={state}
      updateState={updateState}
      exams={exams}
      onGenerate={handleGenerateTest}
      generating={generating}
      error={generateError}
    />,
  ]

  return (
    <AppShell>
      <div className="space-y-6 max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-4 border-b border-border pb-3 sm:pb-4">
          <div className="space-y-0.5">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-foreground">Test Generator</h1>
              <Badge variant="secondary" className="text-[10px] sm:text-xs bg-primary/10 text-primary border-primary/20">
                <Sparkles className="h-3 w-3 mr-1 shrink-0" /> Smart Builder
              </Badge>
            </div>
            <p className="text-xs sm:text-sm text-muted-foreground line-clamp-2 sm:line-clamp-none">
              Automated test paper creation with syllabus mapping, weightage control, and pool validation.
            </p>
          </div>
        </div>

        {/* Stepper Navigation: Modern Clean Mobile & Desktop Presentation */}
        <div>
          {/* Mobile View: Unified Step Indicator + Back / Next Actions */}
          <div className="sm:hidden bg-card border border-border rounded-2xl p-3 shadow-xs space-y-2.5">
            <div className="flex items-center justify-between gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
                disabled={currentStep === 0 || generating}
                className="h-8 px-2.5 text-xs rounded-lg shrink-0"
              >
                <ChevronLeft className="h-3.5 w-3.5 mr-0.5" />
                <span>Back</span>
              </Button>

              <div className="text-center min-w-0 flex-1 px-1">
                <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                  Step {currentStep + 1} of {STEPS.length}
                </div>
                <div className="text-xs font-bold text-foreground truncate">
                  {STEPS[currentStep].label}
                </div>
              </div>

              {currentStep < STEPS.length - 1 ? (
                <Button
                  size="sm"
                  onClick={() => setCurrentStep(currentStep + 1)}
                  disabled={!canGoNext()}
                  className="h-8 px-2.5 text-xs font-semibold rounded-lg shrink-0"
                >
                  <span>Next</span>
                  <ChevronRight className="ml-0.5 h-3.5 w-3.5" />
                </Button>
              ) : (
                <Button
                  size="sm"
                  onClick={handleGenerateTest}
                  disabled={!canGoNext()}
                  className="h-8 px-2.5 text-xs font-bold bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg shrink-0"
                >
                  {generating ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : state.testId ? (
                    <Save className="h-3.5 w-3.5" />
                  ) : (
                    <Rocket className="h-3.5 w-3.5" />
                  )}
                  <span className="ml-1">{state.testId ? 'Save' : 'Generate'}</span>
                </Button>
              )}
            </div>

            {/* Visual 7-Segment Step Progress Bar */}
            <div className="grid grid-cols-7 gap-1 w-full pt-0.5">
              {STEPS.map((_, idx) => {
                const isActive = idx === currentStep
                const isCompleted = idx < currentStep && isStepComplete(idx)
                const isAccessible = isStepAccessible(idx)

                return (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => isAccessible && setCurrentStep(idx)}
                    disabled={!isAccessible}
                    title={`Step ${idx + 1}: ${STEPS[idx].label}`}
                    className={`h-1.5 rounded-full transition-all ${
                      isActive
                        ? 'bg-primary ring-2 ring-primary/30'
                        : isCompleted
                        ? 'bg-primary/70'
                        : 'bg-muted'
                    }`}
                  />
                )
              })}
            </div>
          </div>

          {/* Desktop View: Full 7-Step Horizontal Stepper Grid */}
          <div className="hidden sm:block bg-card border border-border rounded-2xl p-2.5 shadow-xs">
            <div className="grid grid-cols-4 lg:grid-cols-7 gap-2">
              {STEPS.map((step, idx) => {
                const Icon = idx === 6 && state.testId ? Save : step.icon
                const stepLabel = idx === 6 && state.testId ? 'Review & Save' : step.label
                const isActive = idx === currentStep
                const isCompleted = idx < currentStep && isStepComplete(idx)
                const isAccessible = isStepAccessible(idx)

                return (
                  <button
                    key={step.key}
                    onClick={() => isAccessible && setCurrentStep(idx)}
                    disabled={!isAccessible}
                    className={`flex flex-col items-start p-2.5 rounded-xl text-left transition-all duration-200 relative ${
                      isActive
                        ? 'bg-primary text-primary-foreground shadow-xs ring-1 ring-primary'
                        : isAccessible
                        ? 'bg-muted/40 hover:bg-muted text-foreground cursor-pointer border border-transparent'
                        : 'opacity-40 text-muted-foreground bg-transparent cursor-not-allowed'
                    }`}
                  >
                    <div className="flex items-center justify-between w-full mb-1">
                      <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded ${
                        isActive
                          ? 'bg-primary-foreground/20 text-primary-foreground'
                          : isCompleted
                          ? 'bg-primary/10 text-primary'
                          : 'bg-muted text-muted-foreground'
                      }`}>
                        0{idx + 1}
                      </span>
                      {isCompleted && !isActive ? (
                        <div className="h-4 w-4 rounded-full bg-primary/20 text-primary flex items-center justify-center">
                          <Check className="h-2.5 w-2.5" />
                        </div>
                      ) : (
                        <Icon className={`h-4 w-4 ${isActive ? 'text-primary-foreground' : 'text-muted-foreground'}`} />
                      )}
                    </div>
                    <span className="text-xs font-semibold truncate w-full">{stepLabel}</span>
                  </button>
                )
              })}
            </div>
          </div>
        </div>

        {/* Wizard Action Toolbar (Top - Desktop Only) */}
        <div className="hidden sm:flex items-center justify-between gap-2 bg-card border border-border rounded-2xl p-2.5 sm:p-4 shadow-xs">
          <Button
            variant="outline"
            onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
            disabled={currentStep === 0 || generating}
            className="h-9 sm:h-10 px-3.5 sm:px-5 text-xs sm:text-sm shrink-0 rounded-xl"
          >
            <ChevronLeft className="mr-1 sm:mr-1.5 h-4 w-4" />
            <span>Previous Step</span>
          </Button>

          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <span className="text-xs text-muted-foreground hidden lg:inline font-medium">
              Step {currentStep + 1} of {STEPS.length}: {STEPS[currentStep].label}
            </span>
            {currentStep < STEPS.length - 1 ? (
              <Button
                onClick={() => setCurrentStep(currentStep + 1)}
                disabled={!canGoNext()}
                className="h-9 sm:h-10 px-3.5 sm:px-6 text-xs sm:text-sm font-semibold truncate rounded-xl shadow-xs"
              >
                <span>Continue to {STEPS[currentStep + 1].label}</span>
                <ChevronRight className="ml-1 sm:ml-1.5 h-4 w-4 shrink-0" />
              </Button>
            ) : (
              <Button
                onClick={handleGenerateTest}
                disabled={!canGoNext()}
                className="h-9 sm:h-10 px-3.5 sm:px-6 font-bold gap-1.5 text-xs sm:text-sm shadow-xs bg-primary hover:bg-primary/90 text-primary-foreground shrink-0 rounded-xl"
              >
                {generating ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>{state.testId ? 'Saving...' : 'Building...'}</span>
                  </>
                ) : (
                  <>
                    {state.testId ? (
                      <>
                        <Save className="h-4 w-4" />
                        <span>Save ({state.selectedQuestions.length} Qs)</span>
                      </>
                    ) : (
                      <>
                        <Rocket className="h-4 w-4" />
                        <span>Generate ({state.selectedQuestions.length} Qs)</span>
                      </>
                    )}
                  </>
                )}
              </Button>
            )}
          </div>
        </div>

        {/* Step Content Container */}
        <Card className="border border-border shadow-xs rounded-2xl overflow-hidden">
          <CardContent className="p-3.5 sm:p-6">
            {stepComponents[currentStep]}
          </CardContent>
        </Card>

        {/* Wizard Action Footer */}
        <div className="flex items-center justify-between gap-2 bg-card border border-border rounded-2xl p-2.5 sm:p-4 shadow-xs">
          <Button
            variant="outline"
            onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
            disabled={currentStep === 0 || generating}
            className="h-9 sm:h-10 px-3.5 sm:px-5 text-xs sm:text-sm shrink-0 rounded-xl"
          >
            <ChevronLeft className="mr-1 sm:mr-1.5 h-4 w-4" />
            <span className="hidden sm:inline">Previous Step</span>
            <span className="sm:hidden">Back</span>
          </Button>

          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <span className="text-xs text-muted-foreground hidden lg:inline">
              Step {currentStep + 1} of {STEPS.length}: {STEPS[currentStep].label}
            </span>
            {currentStep < STEPS.length - 1 ? (
              <Button
                onClick={() => setCurrentStep(currentStep + 1)}
                disabled={!canGoNext()}
                className="h-9 sm:h-10 px-3.5 sm:px-6 font-semibold text-xs sm:text-sm truncate rounded-xl shadow-xs"
              >
                <span className="hidden sm:inline">Continue to {STEPS[currentStep + 1].label}</span>
                <span className="sm:hidden">Next: {STEPS[currentStep + 1].label}</span>
                <ChevronRight className="ml-1 sm:ml-1.5 h-4 w-4 shrink-0" />
              </Button>
            ) : (
              <Button
                onClick={handleGenerateTest}
                disabled={!canGoNext()}
                className="h-9 sm:h-10 px-3.5 sm:px-6 font-bold gap-1.5 text-xs sm:text-sm shadow-xs bg-primary hover:bg-primary/90 text-primary-foreground shrink-0 rounded-xl"
              >
                {generating ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>{state.testId ? 'Saving...' : 'Building...'}</span>
                  </>
                ) : (
                  <>
                    {state.testId ? (
                      <>
                        <Save className="h-4 w-4" />
                        <span>Save ({state.selectedQuestions.length} Qs)</span>
                      </>
                    ) : (
                      <>
                        <Rocket className="h-4 w-4" />
                        <span>Generate ({state.selectedQuestions.length} Qs)</span>
                      </>
                    )}
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </AppShell>
  )
}
